import {FC} from 'react';
import dayjs, {Dayjs} from 'dayjs';
import {useRecoilValue} from 'recoil';
import {Controller, useFormContext} from 'react-hook-form';
import {DatePicker} from '@mui/x-date-pickers';
import PlaceIcon from '@mui/icons-material/Place';
import {Stack, TextField, Tooltip, Typography} from '@mui/material';
import {currentServiceRequestState, serviceRequestActiveTabState} from '../../../../store/service-requests/atoms';

import {IWorkContentFull, WorkContentType} from '../../../../shared/types/workContent.types';

import {colors} from '../../../../shared/colors';
import {get} from 'lodash';
import {DATE_FORMAT} from '../../../../shared/constants';

interface IProgressItemForm {
  item: {
    type: WorkContentType;
    fields: IWorkContentFull[];
  };
  isEditable: boolean;
}

const labelTypographyStyle = {
  backgroundColor: colors['bg--labelField'],
  border: `1px solid ${colors['border--white--bg']}`,
  textAlign: 'center',
  p: 0.5,
  width: '200px',
};

const inputStyle = {
  width: '310px',
  '& input': {
    fontSize: '0.875rem',
    textAlign: 'center',
    lineHeight: '1.57',
    p: 0.5,
  },
  '& > div': {
    borderRadius: 0,
    p: 0.5,
    height: '100%',
  },
  '& .Mui-disabled': {
    '-webkit-text-fill-color': 'rgba(0, 0, 0, 0.87) !important',
  },
};

function swapCoordinates(text: string) {
  return text.split(', ').reverse().join(',');
}

export const WorkDataReadWriteForm: FC<IProgressItemForm> = ({item, isEditable}) => {
  const serviceRequest = useRecoilValue(currentServiceRequestState);
  const activeTab = useRecoilValue(serviceRequestActiveTabState);

  const {
    control,
    formState: {errors},
  } = useFormContext();

  const isEditMode = isEditable && activeTab?.editMode && !activeTab?.onlyUpload;

  return (
    <Stack style={{flex: 1, marginBottom: 10}}>
      <Stack style={{flex: 1}}>
        {item.fields.map((i: IWorkContentFull) => (
          <Stack direction="row" key={i.id} alignItems="center">
            <Stack sx={labelTypographyStyle} justifyContent="center" alignItems="center" direction="row">
              {isEditMode && i.mandatory ? (
                <>
                  <Tooltip PopperProps={{disablePortal: true}} title="Обязательно для заполнения">
                    <Typography variant="subtitle2" component="div">
                      {i.name}
                    </Typography>
                  </Tooltip>
                  <Typography variant="subtitle2" color="red">
                    *
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="subtitle2" component="div">
                    {i.name}
                  </Typography>
                  {i.mandatory && (
                    <Typography variant="subtitle2" color="red">
                      *
                    </Typography>
                  )}
                </>
              )}
            </Stack>
            {item.type === 'SOURCE_CONTENT' ? (
              <TextField value={serviceRequest?.sourceContent?.[i.modelName]?.value || '-'} disabled sx={inputStyle} />
            ) : (
              <Controller
                name={i.modelName}
                control={control}
                rules={{
                  required: i.mandatory,
                  pattern: i.inputType === 'NUMBER' ? /^[0-9,.-]*$/ : undefined,
                  maxLength: i.inputType !== 'DATE' ? 100 : undefined,
                }}
                render={({field, fieldState: {error}}) => (
                  <>
                    {!isEditMode ? (
                      <TextField
                        value={
                          i.inputType === 'DATE' && 'item' in i && serviceRequest?.workData?.[i.modelName]?.value
                            ? dayjs(
                                serviceRequest?.workData?.[i.modelName]?.value.includes('-') &&
                                  !serviceRequest?.workData?.[i.modelName]?.value.includes(':')
                                  ? new Date(field.value.split('-').reverse().join('-'))
                                  : new Date(field.value),
                              ).format(DATE_FORMAT)
                            : serviceRequest?.workData?.[i.modelName]?.value || '-'
                        }
                        disabled
                        sx={inputStyle}
                      />
                    ) : i.inputType !== 'DATE' ? (
                      <TextField {...field} sx={inputStyle} error={!!errors[i.modelName]} />
                    ) : (
                      <DatePicker
                        slotProps={{
                          textField: {
                            size: 'small',
                            error: !!error?.message,
                            sx: {
                              backgroundColor: 'white',
                              width: 310,
                              '& > div': {
                                height: '100%',
                                fontSize: '14px',
                              },
                              '& input': {
                                paddingLeft: '48px',
                                textAlign: 'center',
                              },
                            },
                          },
                          actionBar: {actions: ['clear', 'accept']},
                        }}
                        format={DATE_FORMAT}
                        value={dayjs(
                          field.value.includes('-') && !field.value.includes(':')
                            ? new Date(field.value.split('-').reverse().join('-'))
                            : new Date(field.value),
                        )}
                        onChange={(value: Dayjs | null) => {
                          const dayjsValue = dayjs(value);
                          const fieldValue = dayjsValue.isValid() ? value?.format('DD-MM-YYYY') : '';
                          field.onChange(fieldValue);
                        }}
                      />
                    )}
                  </>
                )}
              />
            )}
            {i.name?.includes('GPS') && (
              <Tooltip
                title={
                  <>
                    <Typography component="div" variant="subtitle2">
                      Карта
                    </Typography>
                    <Typography component="div" variant="caption">
                      Посмотреть
                    </Typography>
                  </>
                }
              >
                <a
                  target="_blank"
                  href={`https://yandex.ru/maps/?pt=${swapCoordinates(get(i, 'item.text', ''))}&z=16&l=map`}
                  rel="noreferrer"
                >
                  <PlaceIcon color="error" />
                </a>
              </Tooltip>
            )}
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};
